import { useState } from "react";
import { userFacingMessage } from "@veto/domain";
import {
  isBackupPackageV1,
  openBackupPackage,
  type BackupPackageV1,
} from "../messaging/backup";

type BackupPayloadSummary = {
  account?: { displayName?: string };
  contacts?: unknown[];
  conversations?: unknown[];
};

/**
 * Alpha: open/verify an encrypted backup (wrong passphrase must fail closed).
 * Does not replace local identity keys — package has metadata only (§8.4 alpha).
 */
export function BackupImportButton({ onToast }: { onToast: (msg: string) => void }) {
  const [pass, setPass] = useState("");
  const [pkg, setPkg] = useState<BackupPackageV1 | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  return (
    <div className="stack">
      <h4>Open backup</h4>
      <p className="muted text-sm">
        Restores contacts and conversation metadata — private keys and message bodies are never
        included. A wrong passphrase fails closed.
      </p>
      <div className="field">
        <label htmlFor="backupFile">Backup file</label>
        <input
          id="backupFile"
          type="file"
          accept="application/json,.json"
          onChange={(e) => {
            setError(null);
            setSummary(null);
            setPkg(null);
            const file = e.target.files?.[0];
            if (!file) {
              setFileName(null);
              return;
            }
            setFileName(file.name);
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const parsed = JSON.parse(String(reader.result)) as unknown;
                if (!isBackupPackageV1(parsed)) {
                  setError("Not a Veto backup file");
                  return;
                }
                setPkg(parsed);
              } catch {
                setError("Could not parse backup JSON");
              }
            };
            reader.readAsText(file);
          }}
        />
        {fileName && <span className="muted text-sm">{fileName}</span>}
      </div>
      <div className="field">
        <label htmlFor="backupOpenPass">Backup passphrase</label>
        <input
          id="backupOpenPass"
          type="password"
          autoComplete="off"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          placeholder="Passphrase used at export"
        />
      </div>
      {error && (
        <p className="text-danger" role="alert">
          {error}
        </p>
      )}
      {summary && (
        <p className="muted" role="status">
          {summary}
        </p>
      )}
      <button
        className="btn secondary"
        type="button"
        disabled={busy || !pkg || pass.length < 8}
        onClick={() => {
          if (!pkg) return;
          setBusy(true);
          setError(null);
          setSummary(null);
          void (async () => {
            try {
              const opened = (await openBackupPackage(pass, pkg)) as BackupPayloadSummary;
              const contacts = Array.isArray(opened.contacts) ? opened.contacts.length : 0;
              const conversations = Array.isArray(opened.conversations)
                ? opened.conversations.length
                : 0;
              const name = opened.account?.displayName ?? "unknown";
              const line = `Backup opened: ${name} · ${contacts} contacts · ${conversations} conversations (metadata only — identity keys not restored)`;
              setSummary(line);
              onToast(line);
              setPass("");
            } catch (e) {
              const msg = userFacingMessage(e);
              setError(msg);
              onToast(msg);
            } finally {
              setBusy(false);
            }
          })();
        }}
      >
        {busy ? "Opening…" : "Open backup"}
      </button>
    </div>
  );
}
